import EncounterHistory, { ScribeJob } from '@/components/EncounterHistory';
import TodaysSchedule from '@/components/TodaysSchedule';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  clearSelectedAppointment,
  setSelectedAppointment,
} from '@/store/slices/dataSlice';
import { type SimplifiedAppointment } from '@/types/appointment';
import { Calendar, History } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface AppointmentListProps {
  onAppointmentSelect?: (appointment: SimplifiedAppointment) => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  refreshTrigger?: number; // Used to trigger refresh of past encounters
  selectedEncounter?: ScribeJob;
}

const AppointmentList = ({
  onAppointmentSelect,
  activeTab: externalActiveTab,
  onTabChange: externalOnTabChange,
  refreshTrigger,
  selectedEncounter,
}: AppointmentListProps) => {
  const [internalActiveTab, setInternalActiveTab] = useState('today');
  const activeTab = externalActiveTab || internalActiveTab;
  const dispatch = useDispatch();
  const handleAppointmentSelect = (appointment: SimplifiedAppointment) => {
    dispatch(setSelectedAppointment(appointment));
    onAppointmentSelect?.(appointment);
  };

  useEffect(() => {
    if (selectedEncounter) {
      handleEncounterSelect(selectedEncounter);
    }
  }, [selectedEncounter]);

  const handleEncounterSelect = (encounter: ScribeJob) => {
    const appointmentLike: SimplifiedAppointment = {
      scribeJobId: encounter.id,
      appointmentId: encounter.appointment_id,
      patientId: encounter.patient_id,
      patientName: `${encounter.pt_first_name} ${encounter.pt_last_name}`,
      patientFirstName: encounter.pt_first_name,
      patientLastName: encounter.pt_last_name,
      serviceName: encounter.service,
      service: encounter.service,
      providerId: encounter.created_by || 'unknown',
      cptCode: 'N/A',
      patientBirthDate: encounter.pt_dob,
      patientGender: encounter.pt_gender,
      startTime: encounter.appointment_start_time,
      status: encounter.status.toLowerCase(),
      locationId: encounter.location_id,
    };
    dispatch(setSelectedAppointment(appointmentLike));
    onAppointmentSelect?.(appointmentLike);
  };

  const handleTabChange = (tab: string) => {
    if (externalOnTabChange) {
      externalOnTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
    dispatch(clearSelectedAppointment());
  };

  return (
    <div className="flex flex-col h-full">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex flex-col h-full">
        <TabsList className="grid w-full grid-cols-2 bg-gray-300/80 dark:bg-slate-700/60 backdrop-blur-md border border-white/30 dark:border-slate-600/40 rounded-full p-1 mb-3">
          <TabsTrigger
            value="today"
            className={`rounded-full text-md font-medium  transition-all duration-300`}>
            <Calendar className="h-4 w-4 mr-2 hidden md:block" />
            Today's Schedule
          </TabsTrigger>
          <TabsTrigger
            value="past"
            className={`rounded-full text-md font-medium  transition-all duration-300`}>
            <History className="h-4 w-4 mr-2 hidden md:block" />
            Encounter History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="flex-1 mt-0">
          <TodaysSchedule onAppointmentSelect={handleAppointmentSelect} />
        </TabsContent>

        <TabsContent value="past" className="flex-1 mt-0">
          <EncounterHistory
            onEncounterSelect={handleEncounterSelect}
            refreshTrigger={refreshTrigger}
            selectedEncounter={selectedEncounter}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AppointmentList;
